/* ──────────────────────────────────────────────
 *  Stripe redirect return — shared by every public
 *  payment tunnel (booking, ride, gift card, payment
 *  link). Redirect-based methods (Klarna, some 3DS
 *  flows) leave the page: Stripe sends the customer
 *  back to `return_url` with `redirect_status`, and the
 *  tunnel must resume from the URL alone.
 * ────────────────────────────────────────────── */

/**
 * Query parameter carrying the pro_payment uuid on the return URL, one per tunnel: a page can host
 * several tunnels (e.g. a pro website with booking + gift card), the URL must say which one started
 * the payment. Mount points (tabs, modals, widgets) read these to reopen the right tunnel.
 */
export const REDIRECT_RETURN_PARAM = {
  BOOKING: 'cfixe_booking_uuid',
  RIDE: 'cfixe_ride_payment_uuid',
  GIFT_CARD: 'cfixe_gift_payment_uuid',
  /** Gift card tunnel opened in the website modal: the page may also host an inline gift card tunnel. */
  GIFT_CARD_MODAL: 'cfixe_gift_modal_payment_uuid',
  PAYMENT_LINK: 'cfixe_payment_uuid',
} as const;

export type RedirectReturnParam = (typeof REDIRECT_RETURN_PARAM)[keyof typeof REDIRECT_RETURN_PARAM];

/** Parameters Stripe appends to `return_url`. */
const STRIPE_RETURN_PARAMS = [
  'payment_intent',
  'payment_intent_client_secret',
  'setup_intent',
  'setup_intent_client_secret',
  'redirect_status',
] as const;

export interface RedirectReturn {
  /** Stripe `redirect_status`: succeeded | failed | pending (anything else is treated as unknown). */
  status: string;
  /** pro_payment uuid the return is about. */
  uuid: string;
}

/**
 * `return_url` for a payment started by the tunnel owning `param`: the current page, stripped of any
 * previous return (Stripe's or another tunnel's), tagged with the payment uuid. Every other query
 * parameter of the host page is preserved untouched.
 */
export function buildRedirectReturnUrl(param: RedirectReturnParam, uuid: string): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const url = new URL(window.location.href);
  for (const key of STRIPE_RETURN_PARAMS) url.searchParams.delete(key);
  for (const key of Object.values(REDIRECT_RETURN_PARAM)) url.searchParams.delete(key);
  url.searchParams.set(param, uuid);
  return url.toString();
}

/** True when the current URL is a return addressed to the tunnel owning `param` (mount points use it to open that tunnel). */
export function hasRedirectReturn(param: RedirectReturnParam): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).has(param);
}

/**
 * Reads the return addressed to `param` and removes it from the URL (Stripe parameters included), so
 * a reload or a shared link never replays it. `fallback` covers values captured server-side, the URL
 * being possibly already cleaned (StrictMode double effects). Returns null when there is no return.
 */
export function consumeRedirectReturn(
  param: RedirectReturnParam,
  fallback?: Partial<RedirectReturn>,
): RedirectReturn | null {
  if (typeof window === 'undefined') return null;
  const url = new URL(window.location.href);
  const uuid = url.searchParams.get(param) || fallback?.uuid;
  const status = (url.searchParams.has(param) && url.searchParams.get('redirect_status')) || fallback?.status;

  if (url.searchParams.has(param)) {
    url.searchParams.delete(param);
    for (const key of STRIPE_RETURN_PARAMS) url.searchParams.delete(key);
    window.history.replaceState(window.history.state, '', url.toString());
  }

  return uuid && status ? { status, uuid } : null;
}
